import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Snackbar, Alert } from '@mui/material';
import Header from '../../components/Header';
import Appfooter from '../../components/Appfooter';
import Load from '../../components/Load';
import EditCategoryForm from '../../components/admin/category/EditCategoryForm';
import {
  buscarCategoriaTorneioPorId,
  atualizarCategoriaTorneio,
  deletarTodasEntidadesCategoria,
  CategoriaTorneio,
  CategoriaTorneioInput,
} from '../../services/categoriaTorneioService';
import { buscarValidarElegibilidadeTorneio } from '../../services/torneioService';
import { StatusCategoriaTorneio, TipoPontuacao } from '../../types/categoriaTorneio';
import { EditCategoryFormValues, toCategoriaTorneioInput } from '../../schemas/category/categorySchemas';
import { useProfile } from '../../context/ProfileContext';
import { useLigaPermissions } from '../../hooks/useLigaPermissions';
import { PageBreadcrumb, Crumb } from '../../components/navigation';
import { useCategoriaLiga } from '../../hooks/useCategoriaLiga';
import type { CategoriaLiga } from '../../types/categoriaLiga';

interface EditCategoryLocationState {
  torneioId?: string;
  torneioNome?: string;
}

const LeagueEditCategoryTournament: React.FC = () => {
  const { torneioId, categoriaId } = useParams<{ torneioId: string; categoriaId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { torneioId: stateTorneioId, torneioNome: stateTorneioNome } =
    (location.state as EditCategoryLocationState | undefined) || {};

  const { currentProfile, isLoading: profileLoading } = useProfile();
  const ligaId = currentProfile?.tipo === 'LIGA' ? currentProfile.referenciaId : null;
  const { canManageTournaments, isLoading: permissionsLoading } = useLigaPermissions(ligaId);
  const { listar: listarCategoriasLiga } = useCategoriaLiga();

  const [category, setCategory] = useState<CategoriaTorneio | null>(null);
  const [categoriasLigaTemplate, setCategoriasLigaTemplate] = useState<CategoriaLiga[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [torneioElegibilidadeFlag, setTorneioElegibilidadeFlag] = useState<boolean | null>(null);

  const resolvedTorneioId = torneioId || stateTorneioId || '';
  const resolvedTorneioNome =
    category?.torneio?.nome || stateTorneioNome || 'Torneio';

  useEffect(() => {
    let active = true;
    if (!resolvedTorneioId) {
      setTorneioElegibilidadeFlag(null);
      return;
    }
    buscarValidarElegibilidadeTorneio(resolvedTorneioId).then((v) => {
      if (active) setTorneioElegibilidadeFlag(v);
    });
    return () => {
      active = false;
    };
  }, [resolvedTorneioId]);

  const resolvedValidarElegibilidade =
    torneioElegibilidadeFlag ??
    category?.torneio?.validarElegibilidade ??
    false;

  const loadCategory = useCallback(async () => {
    if (!categoriaId) {
      setError('ID da categoria não informado');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await buscarCategoriaTorneioPorId(categoriaId);
      setCategory(data);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Erro ao carregar categoria';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [categoriaId]);

  useEffect(() => {
    loadCategory();
  }, [loadCategory]);

  // Catálogo de CategoriaLiga da liga → templates para o seletor de pré-preenchimento
  // (AC-2). Sem ligaId (torneio fora de liga) o seletor não aparece (AC-5).
  useEffect(() => {
    let active = true;
    if (!ligaId) {
      setCategoriasLigaTemplate([]);
      return;
    }
    listarCategoriasLiga(ligaId)
      .then((list) => {
        if (active) setCategoriasLigaTemplate(list ?? []);
      })
      .catch(() => {
        if (active) setCategoriasLigaTemplate([]);
      });
    return () => {
      active = false;
    };
  }, [ligaId, listarCategoriasLiga]);

  const handleCategorySave = async (values: EditCategoryFormValues) => {
    if (!categoriaId || !resolvedTorneioId) {
      setSnackbar({
        open: true,
        message: 'Torneio não informado',
        severity: 'error',
      });
      return;
    }

    const categoriaTorneioInput = toCategoriaTorneioInput(values, resolvedTorneioId);

    try {
      await atualizarCategoriaTorneio(categoriaId, categoriaTorneioInput);
      setSnackbar({
        open: true,
        message: 'Categoria atualizada com sucesso',
        severity: 'success',
      });
      // Recarregar dados após sucesso
      await loadCategory();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Erro ao atualizar categoria';
      setSnackbar({
        open: true,
        message,
        severity: 'error',
      });
    }
  };

  const handleCancel = () => {
    navigate(`/liga/tournament/${resolvedTorneioId}`);
  };

  const handleCloseSnackbar = () => {
    setSnackbar((s) => ({ ...s, open: false }));
  };

  const hasMatches = category?.partidas && category.partidas.length > 0;

  const handleResetMatches = async () => {
    if (!categoriaId) return;
    
    try {
      await deletarTodasEntidadesCategoria(categoriaId);
      setResetDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'Partidas resetadas com sucesso',
        severity: 'success',
      });
      await loadCategory();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Erro ao resetar partidas';
      setSnackbar({
        open: true,
        message,
        severity: 'error',
      });
    }
  };

  const breadcrumbItems: Crumb[] = useMemo(() => {
    const base: Crumb[] = [
      { label: 'Início', href: '/' },
      { label: 'Liga', href: '/liga/dashboard' },
      { label: 'Torneios', href: '/liga/tournaments' },
    ];

    if (resolvedTorneioId) {
      base.push({
        label: resolvedTorneioNome,
        href: `/liga/tournament/${resolvedTorneioId}`,
      });
    }

    base.push({
      label: category?.nome || 'Categoria',
      href:
        resolvedTorneioId && categoriaId
          ? `/liga/tournament/${resolvedTorneioId}/category/${categoriaId}/manage`
          : undefined,
    });

    return base;
  }, [resolvedTorneioId, resolvedTorneioNome, category?.nome, categoriaId]);

  const initialValues: EditCategoryFormValues & {
    quantidadeFases: number;
    torneioNome: string;
  } = useMemo(() => {
    if (!category) {
      return {
        nome: '',
        descricao: '',
        genero: 'MISTO',
        numeroAtletas: 1,
        precoInscricao: 0,
        regulamento: '',
        tipoGrupoChave: 'GRUPO',
        aplicarDescontoTorneio: false,
        status: StatusCategoriaTorneio.AGENDADA,
        quantidadeFases: 0,
        torneioNome: resolvedTorneioNome,
        quantidadeGrupos: undefined,
        classificadosPorGrupo: undefined,
        tipoPontuacao: undefined,
        numeroSets: undefined,
        gamesPorSets: undefined,
        tiebreaks: undefined,
      };
    }

    return {
      nome: category.nome,
      descricao: category.descricao || '',
      genero: (category.genero as 'MASCULINO' | 'FEMININO' | 'MISTO') || 'MISTO',
      numeroAtletas: category.numeroAtletas ?? 1,
      precoInscricao: category.precoInscricao ?? 0,
      regulamento: category.regulamento || '',
      aplicarDescontoTorneio: category.aplicarDescontoTorneio ?? false,
      // null = "herda do torneio"; preservar explicitamente (não usar ?? false).
      configFilaEspera: category.configFilaEspera ?? null,
      status: (category.status as StatusCategoriaTorneio) || StatusCategoriaTorneio.AGENDADA,
      quantidadeFases: category.fasesCategoria?.length || 0,
      torneioNome: resolvedTorneioNome,
      vagas: category.vagas ?? undefined,
      dataInicio: category.dataInicio || undefined,
      dataFim: category.dataFim || undefined,
      dataLimPag: category.dataLimPag || undefined,
      modeloDisputa: (category.modeloDisputa as CategoriaTorneioInput['modeloDisputa']) || undefined,
      idadeInicial: category.idadeInicial ?? undefined,
      idadeFinal: category.idadeFinal ?? undefined,
      idadeCombinada: category.idadeCombinada ?? undefined,
      quantidadeGrupos: category.quantidadeGrupos ?? undefined,
      classificadosPorGrupo: category.classificadosPorGrupo ?? undefined,
      tamanhoGrupo: category.tamanhoGrupo ?? undefined,
      tipoPontuacao: (category.tipoPontuacao && Object.values(TipoPontuacao).includes(category.tipoPontuacao as TipoPontuacao))
        ? (category.tipoPontuacao as TipoPontuacao)
        : undefined,
      numeroSets: category.numeroSets ?? undefined,
      gamesPorSets: category.gamesPorSets ?? undefined,
      tiebreaks: category.tiebreaks ?? undefined,
      // Semeia o vínculo existente no modo edição (AC-2), quando a query o traz.
      categoriaLigaId:
        (category as CategoriaTorneio & { categoriaLiga?: { id?: string } | null })
          .categoriaLiga?.id ?? undefined,
    };
  }, [category, resolvedTorneioNome]);

  // Se ainda está carregando o perfil, mostra loading
  if (profileLoading || permissionsLoading) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <Load />
                </div>
              </div>
            </div>
          </div>
        </div>
        <Appfooter />
      </>
    );
  }

  // Se não for perfil de liga, não renderiza nada
  if (!currentProfile || currentProfile.tipo !== 'LIGA') {
    return null;
  }

  // Verificar permissão de gerenciamento de torneios
  if (!canManageTournaments) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3 mt-3">
                    <div className="card-body p-4">
                      <div className="alert alert-warning" role="alert">
                        <h5 className="alert-heading">Acesso Negado</h5>
                        <p className="mb-0">
                          Você não tem permissão para gerenciar torneios desta Liga. Apenas
                          administradores e gerentes podem realizar esta ação.
                        </p>
                      </div>
                      <button
                        className="btn btn-primary mt-3"
                        onClick={() => navigate('/liga/dashboard')}
                      >
                        Voltar ao Dashboard
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Appfooter />
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <Load />
                </div>
              </div>
            </div>
          </div>
        </div>
        <Appfooter />
      </>
    );
  }

  if (error || !category) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3 mt-3">
                    <div className="card-body p-4">
                      <div className="alert alert-danger" role="alert">
                        {error || 'Categoria não encontrada'}
                      </div>
                      <button
                        className="btn btn-primary mt-3"
                        onClick={() => navigate(`/liga/tournament/${resolvedTorneioId}`)}
                      >
                        Voltar ao Torneio
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Appfooter />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left">
            <div className="row feed-body">
              <div className="col-xl-12">
                <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3 mt-3">
                  <div className="card-body p-4">
                    <PageBreadcrumb
                      items={breadcrumbItems}
                      onBack={handleCancel}
                      variant="bootstrap"
                    />
                    <h3 className="fw-700 mb-4 mt-3">Editar Categoria de Torneio</h3>
                    {hasMatches && canManageTournaments && (
                      <div className="mb-3">
                        <button
                          type="button"
                          className="btn btn-danger"
                          onClick={() => setResetDialogOpen(true)}
                        >
                          Resetar partidas
                        </button>
                      </div>
                    )}
                    <EditCategoryForm
                      initialValues={initialValues}
                      onSubmit={handleCategorySave}
                      onCancel={handleCancel}
                      hasMatches={hasMatches}
                      modeloDisputa={category?.modeloDisputa}
                      validarElegibilidade={resolvedValidarElegibilidade}
                      categoriasLigaTemplate={categoriasLigaTemplate}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Appfooter />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {resetDialogOpen && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setResetDialogOpen(false)} />
          <div
            className="modal fade show d-block"
            role="dialog"
            tabIndex={-1}
            style={{ overflow: 'auto' }}
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirmar Reset de Partidas</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Fechar"
                    onClick={() => setResetDialogOpen(false)}
                  />
                </div>
                <div className="modal-body">
                  <p>
                    Tem certeza que deseja resetar todas as partidas desta categoria? Esta ação irá deletar todas as partidas e fases atuais. Esta ação não pode ser desfeita.
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setResetDialogOpen(false)}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleResetMatches}
                  >
                    Confirmar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default LeagueEditCategoryTournament;

